import { useCallback, useEffect, useMemo, type ChangeEvent } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import {
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Clock,
  Newspaper,
  RotateCcw,
  UserRound
} from 'lucide-react';

import { getBlogPostPath } from '@/features/Blog/blogPosts';
import { useBlogPosts } from '@/features/Blog/useBlogContent';
import Footer from '@/shared/components/Footer';
import Navbar from '@/shared/components/Navbar';
import { Badge } from '@/shared/components/ui/Badge';
import { ButtonArrow } from '@/shared/components/ui/ButtonArrow';
import { Button } from '@/shared/components/ui/Button';
import { Select, SelectOption } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useI18n } from '@/shared/contexts/I18nProvider';
import { getAbsoluteUrl, usePageSeo } from '@/shared/hooks';
import { cn } from '@/shared/utils';

const postsPerPage = 9;
const allFilterValue = 'all';

type ActiveFilters = {
  industry: string;
  service: string;
  type: string;
};

type FilterOption = {
  label: string;
  value: string;
};

const mergeAvailableOptions = (
  configuredOptions: readonly FilterOption[],
  availableValues: ReadonlyMap<string, string>
) => {
  const configuredValues = new Set(
    configuredOptions.map(option => option.value)
  );

  return [
    ...configuredOptions.filter(option => availableValues.has(option.value)),
    ...Array.from(availableValues, ([value, label]) => ({
      label,
      value
    })).filter(option => !configuredValues.has(option.value))
  ];
};

const isPositivePageNumber = (value: string | undefined) => {
  if (!value) {
    return false;
  }

  return /^[1-9]\d*$/.test(value);
};

const createFilterSearch = (filters: ActiveFilters) => {
  const params = new URLSearchParams();

  if (filters.type !== allFilterValue) {
    params.set('type', filters.type);
  }

  if (filters.service !== allFilterValue) {
    params.set('service', filters.service);
  }

  if (filters.industry !== allFilterValue) {
    params.set('industry', filters.industry);
  }

  const search = params.toString();

  return search ? `?${search}` : '';
};

const getFilterValue = (
  searchParams: URLSearchParams,
  key: keyof ActiveFilters,
  validValues: readonly string[]
) => {
  const value = searchParams.get(key);

  if (value !== null && validValues.includes(value)) {
    return value;
  }

  return allFilterValue;
};

const createLocalDate = (dateString: string) => {
  const [year = 0, month = 1, day = 1] = dateString.split('-').map(Number);

  return new Date(year, month - 1, day);
};

const InsightsPage = () => {
  const { content, language, locale, localizedPath } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const insights = content.insights;
  const filters = insights.filters;
  const { isLoading: isBlogFeedLoading, posts: orderedPosts } =
    useBlogPosts(language);
  const availableFilterOptions = useMemo(() => {
    const availableTypes = new Map(
      orderedPosts.map(post => [post.kind, post.kind])
    );
    const availableServices = new Map(
      orderedPosts.map(post => [post.serviceKey, post.service])
    );
    const availableIndustries = new Map(
      orderedPosts.map(post => [post.industryKey, post.industry])
    );

    return {
      industryOptions: mergeAvailableOptions(
        filters.industryOptions,
        availableIndustries
      ),
      serviceOptions: mergeAvailableOptions(
        filters.serviceOptions,
        availableServices
      ),
      typeOptions: mergeAvailableOptions(filters.typeOptions, availableTypes)
    };
  }, [
    filters.industryOptions,
    filters.serviceOptions,
    filters.typeOptions,
    orderedPosts
  ]);
  const typeValues = availableFilterOptions.typeOptions.map(
    option => option.value
  );
  const serviceValues = availableFilterOptions.serviceOptions.map(
    option => option.value
  );
  const industryValues = availableFilterOptions.industryOptions.map(
    option => option.value
  );
  const selectedFilters: ActiveFilters = {
    type: getFilterValue(searchParams, 'type', typeValues),
    service: getFilterValue(searchParams, 'service', serviceValues),
    industry: getFilterValue(searchParams, 'industry', industryValues)
  };
  const hasActiveFilters = Object.values(selectedFilters).some(
    value => value !== allFilterValue
  );
  const filterSearch = createFilterSearch(selectedFilters);

  const requestedPage = isPositivePageNumber(params.pageNumber)
    ? Number(params.pageNumber)
    : 1;
  const filteredPosts = useMemo(() => {
    return orderedPosts.filter(post => {
      const matchesType =
        selectedFilters.type === allFilterValue ||
        post.kind === selectedFilters.type;
      const matchesService =
        selectedFilters.service === allFilterValue ||
        post.serviceKey === selectedFilters.service;
      const matchesIndustry =
        selectedFilters.industry === allFilterValue ||
        post.industryKey === selectedFilters.industry;

      return matchesType && matchesService && matchesIndustry;
    });
  }, [
    orderedPosts,
    selectedFilters.industry,
    selectedFilters.service,
    selectedFilters.type
  ]);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredPosts.length / postsPerPage)
  );
  const currentPage = Math.min(requestedPage, totalPages);
  const pageStartIndex = (currentPage - 1) * postsPerPage;
  const visiblePosts = useMemo(
    () => filteredPosts.slice(pageStartIndex, pageStartIndex + postsPerPage),
    [filteredPosts, pageStartIndex]
  );
  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index + 1),
    [totalPages]
  );
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
    [locale]
  );
  const typeLabelByValue = useMemo(
    () =>
      new Map(filters.typeOptions.map(option => [option.value, option.label])),
    [filters.typeOptions]
  );
  const defaultSeo = useMemo(
    () => ({
      description: content.meta.description,
      imageAlt: content.meta.imageAlt,
      keywords: content.meta.keywords,
      title: content.meta.title,
      twitterDescription: content.meta.twitterDescription
    }),
    [content.meta]
  );
  const seoConfig = useMemo(() => {
    const pagePath =
      currentPage <= 1 ? '/insights' : `/insights/page/${currentPage}`;
    const canonicalPath = hasActiveFilters ? '/insights' : pagePath;
    const canonicalUrl = getAbsoluteUrl(canonicalPath);

    return {
      canonicalPath,
      description: insights.description,
      imageAlt: content.meta.imageAlt,
      imageHeight: '935',
      imagePath: '/images/datasliv_dark.jpg',
      imageType: 'image/jpeg',
      imageWidth: '1800',
      keywords: `${content.meta.keywords}, DataSliv blog, DataSliv insights, on-prem data blog`,
      locale,
      robots: hasActiveFilters ? 'noindex, follow' : 'index, follow',
      siteName: content.common.dataSliv,
      title:
        currentPage <= 1
          ? `${insights.allInsights} | DataSliv`
          : `${insights.allInsights} - ${filters.page} ${currentPage} | DataSliv`,
      structuredData: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@id': `${canonicalUrl}#webpage`,
            '@type': 'CollectionPage',
            description: insights.description,
            inLanguage: locale,
            name: insights.allInsights,
            url: canonicalUrl
          },
          {
            '@id': `${canonicalUrl}#itemlist`,
            '@type': 'ItemList',
            itemListElement: visiblePosts.map((post, index) => ({
              '@type': 'ListItem',
              name: post.title,
              position: pageStartIndex + index + 1,
              url: getAbsoluteUrl(getBlogPostPath(post))
            }))
          }
        ]
      }
    };
  }, [
    content.meta.imageAlt,
    content.meta.keywords,
    content.common.dataSliv,
    currentPage,
    filters.page,
    hasActiveFilters,
    insights.allInsights,
    insights.description,
    locale,
    pageStartIndex,
    visiblePosts
  ]);

  usePageSeo(seoConfig, defaultSeo);

  const getPagePath = useCallback(
    (page: number) => {
      const pagePath = page <= 1 ? '/insights' : `/insights/page/${page}`;

      return `${localizedPath(pagePath)}${filterSearch}`;
    },
    [filterSearch, localizedPath]
  );

  useEffect(() => {
    if (requestedPage === currentPage) {
      return;
    }

    void navigate(getPagePath(currentPage), { replace: true });
  }, [currentPage, getPagePath, navigate, requestedPage]);

  useEffect(() => {
    if (!location.hash) {
      return undefined;
    }

    const targetId = decodeURIComponent(location.hash.slice(1));
    const frameId = window.requestAnimationFrame(() => {
      document
        .getElementById(targetId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [location.hash, location.key]);

  const handleFilterChange =
    (key: keyof ActiveFilters) => (event: ChangeEvent<HTMLSelectElement>) => {
      const nextFilters = {
        ...selectedFilters,
        [key]: event.target.value
      };

      void navigate(
        `${localizedPath('/insights')}${createFilterSearch(nextFilters)}`
      );
    };

  const resetFilters = () => {
    void navigate(localizedPath('/insights'));
  };

  return (
    <div className="min-h-screen bg-background-primary text-text-primary antialiased">
      <Navbar />

      <main>
        <section className="relative overflow-hidden border-b border-border-muted bg-background-primary">
          <img
            src="/images/datasliv_dark.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-[0.18] dark:opacity-30"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-background-primary/88" />

          <div className="relative z-10 mx-auto max-w-7xl px-4 pb-16 pt-36 sm:px-6 sm:pb-20 sm:pt-40 lg:px-8">
            <Badge
              variant="outline"
              className="mb-5 border-accent-primary/30 bg-background-card/70 px-4 py-2 font-mono uppercase tracking-[0.18em] text-accent-primary shadow-lg shadow-shadow backdrop-blur"
            >
              {insights.heroBadge}
            </Badge>
            <h1 className="max-w-4xl text-[clamp(2.35rem,5vw,4.8rem)] font-light leading-[1.02] tracking-tight text-text-primary">
              {insights.title}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-text-secondary sm:text-xl">
              {insights.description}
            </p>
          </div>
        </section>

        <section
          id="latest-insights"
          className="scroll-mt-32 border-b border-border-muted bg-background-secondary py-16 sm:py-20"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="font-mono text-sm uppercase tracking-[0.2em] text-accent-primary">
                  {insights.latestLabel}
                </p>
                <h2 className="mt-3 text-3xl font-light tracking-tight text-text-primary sm:text-4xl">
                  {insights.allInsights}
                </h2>
              </div>

              <div className="flex flex-wrap gap-2">
                {insights.quickLinks.map(link => (
                  <Button key={link.label} asChild variant="outline">
                    <Link
                      to={localizedPath(link.to)}
                      className="gap-2 font-mono"
                    >
                      {link.label}
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </Button>
                ))}
              </div>
            </div>

            <div className="mt-8 rounded-lg border border-border-primary bg-background-card p-4 shadow-sm shadow-shadow sm:p-5">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-end">
                <div>
                  <h3 className="text-xl font-medium text-text-primary">
                    {filters.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-text-secondary">
                    {filters.description}
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="block">
                    <span className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                      {filters.typeLabel}
                    </span>
                    <Select
                      value={selectedFilters.type}
                      onChange={handleFilterChange('type')}
                      className="mt-2"
                    >
                      <SelectOption value={allFilterValue}>
                        {filters.allTypes}
                      </SelectOption>
                      {availableFilterOptions.typeOptions.map(option => (
                        <SelectOption key={option.value} value={option.value}>
                          {option.label}
                        </SelectOption>
                      ))}
                    </Select>
                  </label>

                  <label className="block">
                    <span className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                      {filters.serviceLabel}
                    </span>
                    <Select
                      value={selectedFilters.service}
                      onChange={handleFilterChange('service')}
                      className="mt-2"
                    >
                      <SelectOption value={allFilterValue}>
                        {filters.allServices}
                      </SelectOption>
                      {availableFilterOptions.serviceOptions.map(option => (
                        <SelectOption key={option.value} value={option.value}>
                          {option.label}
                        </SelectOption>
                      ))}
                    </Select>
                  </label>

                  <label className="block">
                    <span className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                      {filters.industryLabel}
                    </span>
                    <Select
                      value={selectedFilters.industry}
                      onChange={handleFilterChange('industry')}
                      className="mt-2"
                    >
                      <SelectOption value={allFilterValue}>
                        {filters.allIndustries}
                      </SelectOption>
                      {availableFilterOptions.industryOptions.map(option => (
                        <SelectOption key={option.value} value={option.value}>
                          {option.label}
                        </SelectOption>
                      ))}
                    </Select>
                  </label>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 border-t border-border-muted pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                  {filteredPosts.length} {filters.results}
                </p>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-fit gap-2 font-mono"
                  disabled={!hasActiveFilters}
                  onClick={resetFilters}
                >
                  <RotateCcw className="h-4 w-4" />
                  {filters.reset}
                </Button>
              </div>
            </div>

            {isBlogFeedLoading ? (
              <div
                className="mt-8 grid gap-5 lg:grid-cols-3"
                role="status"
                aria-label={insights.loadingPosts}
              >
                {Array.from({ length: 3 }, (_, index) => (
                  <div
                    key={index}
                    className="overflow-hidden rounded-lg border border-border-primary bg-background-card"
                  >
                    <Skeleton className="aspect-[16/10] w-full rounded-none" />
                    <div className="space-y-4 p-5">
                      <Skeleton className="h-5 w-2/3" />
                      <Skeleton className="h-7 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : visiblePosts.length > 0 ? (
              <div className="mt-8 grid gap-5 lg:grid-cols-3">
                {visiblePosts.map(post => {
                  const PostKindIcon =
                    post.kind === 'news' ? Newspaper : BookOpen;

                  return (
                    <Link
                      key={post.slug}
                      id={post.slug}
                      to={localizedPath(getBlogPostPath(post))}
                      className="group scroll-mt-32 overflow-hidden rounded-lg border border-border-primary bg-background-card shadow-sm shadow-shadow transition-colors hover:border-accent-primary/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                    >
                      <div className="aspect-[16/10] overflow-hidden border-b border-border-muted bg-background-primary">
                        <img
                          src={post.imageSrc}
                          alt={post.imageAlt}
                          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-text-tertiary">
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-primary/10 px-2 py-1 text-accent-primary">
                            <PostKindIcon className="h-3.5 w-3.5" />
                            {typeLabelByValue.get(post.kind) ?? post.kind}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {dateFormatter.format(
                              createLocalDate(post.publishedAt)
                            )}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            {post.readTime}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <UserRound className="h-3.5 w-3.5" />
                            {post.authorName}
                          </span>
                        </div>

                        <p className="mt-4 font-mono text-xs uppercase tracking-[0.16em] text-accent-primary">
                          {post.category}
                        </p>
                        <h3 className="mt-2 text-xl font-medium leading-7 text-text-primary">
                          {post.title}
                        </h3>

                        <div className="mt-5 flex flex-wrap gap-2 font-mono text-[0.68rem] uppercase tracking-[0.14em] text-text-tertiary">
                          <span className="rounded-md border border-border-muted px-2.5 py-1">
                            {post.service}
                          </span>
                          <span className="rounded-md border border-border-muted px-2.5 py-1">
                            {post.industry}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="mt-8 rounded-lg border border-border-primary bg-background-card p-8 text-center shadow-sm shadow-shadow">
                <h3 className="text-2xl font-light text-text-primary">
                  {orderedPosts.length === 0
                    ? insights.emptyFeedTitle
                    : filters.emptyTitle}
                </h3>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-text-secondary">
                  {orderedPosts.length === 0
                    ? insights.emptyFeedDescription
                    : filters.emptyDescription}
                </p>
              </div>
            )}

            {totalPages > 1 && (
              <nav
                aria-label={filters.page}
                className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <p className="font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
                  {filters.page} {currentPage} {filters.of} {totalPages}
                </p>

                <div className="flex flex-wrap items-center gap-2">
                  <Button asChild variant="outline" size="sm">
                    <Link
                      to={getPagePath(Math.max(1, currentPage - 1))}
                      aria-disabled={currentPage === 1}
                      className={cn(
                        'gap-2 font-mono',
                        currentPage === 1 && 'pointer-events-none opacity-50'
                      )}
                    >
                      {filters.previous}
                    </Link>
                  </Button>

                  {pageNumbers.map(pageNumber => (
                    <Link
                      key={pageNumber}
                      to={getPagePath(pageNumber)}
                      aria-current={
                        pageNumber === currentPage ? 'page' : undefined
                      }
                      className={cn(
                        'inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-3 font-mono text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                        pageNumber === currentPage
                          ? 'border-accent-primary bg-accent-primary text-text-inverse'
                          : 'border-border-primary bg-background-card text-text-primary hover:bg-background-hover'
                      )}
                    >
                      {pageNumber}
                    </Link>
                  ))}

                  <Button asChild variant="outline" size="sm">
                    <Link
                      to={getPagePath(Math.min(totalPages, currentPage + 1))}
                      aria-disabled={currentPage === totalPages}
                      className={cn(
                        'gap-2 font-mono',
                        currentPage === totalPages &&
                          'pointer-events-none opacity-50'
                      )}
                    >
                      {filters.next}
                    </Link>
                  </Button>
                </div>
              </nav>
            )}
          </div>
        </section>

        <section className="bg-background-primary py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 border-y border-border-muted py-10 lg:grid-cols-[minmax(0,0.95fr)_auto] lg:items-center">
              <div>
                <h2 className="max-w-3xl text-3xl font-light tracking-tight text-text-primary sm:text-4xl">
                  {insights.finalCta.title}
                </h2>
                <p className="mt-4 max-w-3xl text-base leading-7 text-text-secondary">
                  {insights.finalCta.description}
                </p>
              </div>

              <Button asChild size="lg" className="w-fit gap-2 font-mono">
                <Link to={localizedPath('/contact')}>
                  {insights.finalCta.contact}
                  <ButtonArrow className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default InsightsPage;
